import { CommonModule, NgClass } from "@angular/common";
import {
  ChangeDetectionStrategy,
  booleanAttribute,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { MAX_ASINS_PER_STRATEGY } from "@front/m19-injection-token";
import { Catalog, ProductEx } from "@front/m19-models";
import { AccountSelectionService, AsinService } from "@front/m19-services";
import {
  ClipboardService,
  ConfirmPopupComponent,
  ConfirmPopupData,
  EmptyStateComponent,
  IButtonDirective,
  ICheckboxComponent,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IPaginationConfig,
  IPaginatorComponent,
  ITooltipDirective,
  ModalService,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { getCsvFileName } from "@m19-board/grid-config/grid-config";
import { CsvExportService, CsvField, fieldExtractor, simpleField } from "@m19-board/services/csv-export.service";
import { AsinSelectionRowComponent } from "@m19-board/shared/asin-selection/asin-selection-row.component";
import { AsinFilter, AsinFilterService } from "@m19-board/shared/filter/asin-filter.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, combineLatest, filter, forkJoin, map, of, switchMap, take } from "rxjs";
import { BulkAsinModalComponent } from "../bulk-asin-modal/bulk-asin-modal.component";
import { CompactFilterComponent, Filter } from "../compact-filter/compact-filter.component";

export interface AsinSelectionProduct {
  asin: string;
  title: string | undefined;
  brand: string | undefined;
  childAsins: string[] | undefined;
  image: string | undefined;
  tag1?: string | undefined;
  tag2?: string | undefined;
  ineligibleReason?: string | undefined;
  isDisabled: boolean;
  isSelected: boolean;
  isOtherCatalog: boolean;
  isUnmanaged: boolean;
  isParent: boolean;
  isIndeterminate: boolean; // used for parent checkbox state
}

@UntilDestroy()
@Component({
  selector: "app-asin-selection",
  imports: [
    CommonModule,
    ITooltipDirective,
    NgClass,
    ICheckboxComponent,
    SpinnerComponent,
    IButtonDirective,
    TranslocoModule,
    FormsModule,
    EmptyStateComponent,
    AsinSelectionRowComponent,
    IInputGroupDirective,
    IInputGroupInput,
    CompactFilterComponent,
    IPaginatorComponent,
    IIconComponent,
  ],
  providers: [AsinFilterService],
  templateUrl: "./asin-selection.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AsinSelectionComponent {
  private readonly asinService = inject(AsinService);
  private readonly accountService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly csvExportService = inject(CsvExportService);
  private readonly asinFilterService = inject(AsinFilterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly maxAsinsPerStrategy = inject<number>(MAX_ASINS_PER_STRATEGY);

  // instead of catalog (default), provide custom list of ASINs to select from
  customList = input<string[] | undefined>(undefined);

  disabledAsins = input<Set<string>>(new Set());

  selectedAsins = model<string[]>([]);

  // allow other catalog ASINs to be selected (used in bulk mode)
  allowOtherCatalog = input<boolean>(false);

  asinsLimit = input<number>(this.maxAsinsPerStrategy);

  csvExport = input<boolean>(true);

  readonly = input<boolean>(false);

  withBulk = input(true, { transform: booleanAttribute });
  withGroupByParent = input(true, { transform: booleanAttribute });
  bulkButtonLabel = input<string>(this.translocoService.translate("common.bulk"));
  readonly ineligibilityTag = input<"SP" | "SB" | undefined>(undefined);
  bordered = input<boolean>(true);

  // constants
  readonly DEFAULT_PAGE_SIZE = 25;
  readonly NO_IMAGE_URL_PART = "no-img-sm.";
  readonly toastrService = inject(ToasterService);
  readonly clipboard = inject(ClipboardService);
  readonly router = inject(Router);

  // filters
  readonly searchFilter = signal<string>("");
  readonly onlySelectedFilter = signal<boolean>(false);

  filters = toSignal(
    this.asinFilterService.filters$.pipe(
      filter((filter) => filter.length > 0),
      take(1),
    ),
  );
  filteredAsins = toSignal(this.asinFilterService.filteredAsins$, { requireSync: true });

  // state
  readonly accountMarketplace = toSignal(this.accountService.singleAccountMarketplaceSelection$);
  readonly isLoading = computed<boolean>(() => this.catalog() === undefined);
  readonly noCatalog = computed<boolean>(() => this.catalog()?.asinOffers.size === 0);
  readonly hasReachedAsinLimit = computed<boolean>(
    () => !!(this.asinsLimit() && this.selectedAsins().length >= this.asinsLimit()!),
  );

  readonly groupByParentAsin = signal<boolean>(false);
  readonly openedParentAsin = signal<string | undefined>(undefined);

  readonly pagination = signal<IPaginationConfig>({
    pageIndex: 0,
    pageSize: this.DEFAULT_PAGE_SIZE,
    length: 0,
  });

  private otherCatalogAsins = signal<string[]>([]);

  private readonly refreshCatalog$ = new BehaviorSubject<void>(undefined);
  private readonly catalog = toSignal(
    combineLatest([this.accountService.singleAccountMarketplaceSelection$, this.refreshCatalog$]).pipe(
      untilDestroyed(this),
      switchMap(([am]) => this.asinService.getCatalog(am.accountId, am.marketplace)),
    ),
  );

  private readonly asinsNotInCatalog = computed<string[]>(
    () => {
      const catalog = this.catalog();
      if (catalog) {
        // selected ASIN not in catalog should be added to display it in the list
        const selectedAsinsNotInCatalog = this.selectedAsins().filter((asin) => !catalog?.contains(asin));
        const parentAsins = Array.from(catalog.parentAsins.keys()).filter((asin) => !catalog?.contains(asin));
        return [...new Set(selectedAsinsNotInCatalog.concat(this.otherCatalogAsins()).concat(parentAsins))];
      }
      return [];
    },
    { equal: (a, b) => a.join() === b.join() },
  );

  private readonly notInCatalogProductDetails$ = toObservable(this.asinsNotInCatalog).pipe(
    switchMap((asinsNotInCatalog) => {
      if (asinsNotInCatalog.length === 0) {
        return of(new Map<string, ProductEx>());
      }
      return forkJoin(
        asinsNotInCatalog.map((asin) =>
          this.asinService.getProductWithMarketplace(asin, this.accountMarketplace()!.marketplace),
        ),
      ).pipe(
        map((products) => {
          const productDetailsMap = new Map<string, ProductEx>();
          for (const product of products) {
            if (product && product.asin) {
              productDetailsMap.set(product.asin, product);
            }
          }
          return productDetailsMap;
        }),
      );
    }),
  );

  private readonly notInCatalogProductDetails = toSignal(this.notInCatalogProductDetails$, {
    initialValue: new Map<string, ProductEx>(),
  });

  private readonly baseAsinList = computed<string[]>(() => {
    if (this.catalog()) {
      const asinsNotInCatalog = this.asinsNotInCatalog();

      let res: string[];

      if (this.customList()) {
        res = this.customList()!.concat(asinsNotInCatalog);
      } else {
        res = asinsNotInCatalog.concat(Array.from(this.catalog()!.asinOffers.keys()));
      }

      return [...new Set(res)];
    }
    return [];
  });

  private readonly managedOrUnmanagedAsins$ = toObservable(this.baseAsinList).pipe(
    switchMap((asins) => {
      if (asins.length === 0) {
        return of(new Set<string>());
      }
      return this.asinService
        .filterAsinsInInventory(this.accountMarketplace()!.accountId, this.accountMarketplace()!.marketplace, asins)
        .pipe(map((resp) => new Set(resp)));
    }),
  );
  private readonly managedOrUnmanagedAsins = toSignal(this.managedOrUnmanagedAsins$, {
    initialValue: new Set<string>(),
  });

  readonly parentProductMap = computed<Map<AsinSelectionProduct, AsinSelectionProduct[]>>(() => {
    const catalog = this.catalog();
    if (!catalog) return new Map();
    const res: Map<string, AsinSelectionProduct[]> = new Map();
    for (const asin of this.baseAsinList()) {
      const parent = catalog.getParentAsin(asin) ?? asin;
      if (catalog.isParentAsin(asin)) continue;

      if (res.has(parent)) {
        res.get(parent)!.push(this.getProduct(asin, this.disabledAsins(), this.selectedAsins(), catalog));
      } else {
        res.set(parent, [this.getProduct(asin, this.disabledAsins(), this.selectedAsins(), catalog)]);
      }
    }

    const productList = new Map<AsinSelectionProduct, AsinSelectionProduct[]>();
    res.forEach((value, key) => {
      productList.set(this.getProduct(key, this.disabledAsins(), this.selectedAsins(), catalog), value);
    });
    return productList;
  });

  readonly filteredProductList = computed<Map<AsinSelectionProduct, AsinSelectionProduct[]>>(() => {
    let products: [AsinSelectionProduct, AsinSelectionProduct[]][] = Array.from(
      new Map(this.parentProductMap()).entries(),
    );

    const hasOnlySelectedFilter = this.onlySelectedFilter();
    const searchText = this.searchFilter();

    const filteredAsins = this.filteredAsins();

    const productPassesFilters = (p: AsinSelectionProduct): boolean => {
      if (hasOnlySelectedFilter && !p.isSelected) return false;
      if (!!searchText && !this.isPassingFilter(p, searchText)) return false;

      const hasFilteredAsins = !!filteredAsins;

      return hasFilteredAsins ? filteredAsins.has(p.asin) : true;
    };

    products = products
      .map<[AsinSelectionProduct, AsinSelectionProduct[]]>(([parentProduct, childProducts]) => {
        if (productPassesFilters(parentProduct)) return [parentProduct, childProducts];

        const filteredProduct: [AsinSelectionProduct, AsinSelectionProduct[]] = [
          parentProduct,
          childProducts.filter((p) => productPassesFilters(p)),
        ];
        return filteredProduct;
      })
      .filter(([_, childProducts]) => childProducts.length > 0);

    return new Map(Array.from(products));
  });

  readonly paginatedProductList = computed<Map<AsinSelectionProduct, AsinSelectionProduct[]>>(() => {
    const list = Array.from(this.filteredProductList().values()).flat();

    if (this.groupByParentAsin()) {
      if (this.filteredProductList().size <= this.DEFAULT_PAGE_SIZE) return this.filteredProductList();
    } else {
      if (list.length <= this.DEFAULT_PAGE_SIZE) return this.filteredProductList();
    }

    const start = this.pagination().pageIndex * this.pagination().pageSize;
    const end = start + this.pagination().pageSize;

    if (this.groupByParentAsin()) {
      return new Map(Array.from(this.filteredProductList()).slice(start, end));
    }
    return new Map([[list[0], list.slice(start, end)]]);
  });

  readonly filteredProductLength = computed<number>(() => {
    if (this.groupByParentAsin()) {
      return this.filteredProductList().size;
    }
    return Array.from(this.filteredProductList().values()).flat().length;
  });

  readonly selectedProductList = computed<AsinSelectionProduct[]>(() => {
    return Array.from(this.parentProductMap().values())
      .flat()
      .filter((p) => p.isSelected);
  });

  readonly isAllSelected = computed<boolean>(() => {
    const allEnabledAsinsAreSelected = Array.from(this.filteredProductList().values())
      .flat()
      .filter((p) => !p.isDisabled)
      .every((product) => product.isSelected);

    return this.filteredProductList().size > 0 && allEnabledAsinsAreSelected;
  });

  readonly isCheckboxIndeterminate = computed<boolean>(() => {
    return this.selectedAsins().length > 0 && !this.isAllSelected();
  });

  readonly checkboxAllTooltip = computed<string>(() => {
    if (this.filteredProductList().size === 0) {
      return "";
    }
    return this.isAllSelected() ? "Unselect all" : "Select all";
  });

  onProductClick(product: AsinSelectionProduct) {
    if (product.isDisabled || this.readonly()) return;
    if (product.isSelected) {
      if (product.isParent) {
        this.selectedAsins.update((s) => s.filter((asin) => !product.childAsins!.includes(asin)));
      } else {
        this.selectedAsins.update((s) => s.filter((asin) => asin !== product.asin));
      }
    } else if (!this.hasReachedAsinLimit()) {
      if (product.isParent) {
        this.selectedAsins.update((s) => [...s, ...product.childAsins!]);
      } else {
        this.selectedAsins.update((s) => [...s, product.asin]);
      }
    }
  }

  restoreToManagedCatalog(product: AsinSelectionProduct) {
    if (this.readonly()) return;
    const confirmation = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      data: {
        message: this.translocoService.translate("asin-selection.restore_to_managed_catalog_confirm"),
        color: "orange",
      },
    });
    confirmation
      .afterClosed()
      .pipe(
        switchMap((confirm) => {
          if (confirm) {
            return this.asinService.addAsins(
              this.accountMarketplace()!.accountId,
              this.accountMarketplace()!.marketplace,
              [product.asin],
            );
          }
          return of(null);
        }),
      )
      .subscribe({
        next: (res) => {
          if (res === null) {
            return;
          }
          this.refreshCatalog$.next();
          this.toastrService.success(
            this.translocoService.translate("asin-selection.product_restored_to_managed_catalog"),
          );
        },
        error: () => {
          this.toastrService.error(
            this.translocoService.translate("asin-selection.product_restored_to_managed_catalog_error"),
          );
        },
      });
  }

  selectAllChange() {
    if (this.isAllSelected() || this.selectedAsins().length >= this.asinsLimit()!) {
      this.unselectAll();
    } else {
      this.selectAll();
    }
  }

  onSearch(search: string) {
    this.searchFilter.set(search);
    this.pagination.update((p) => ({ ...p, pageIndex: 0 }));
  }

  openBulkAsinModal() {
    const ref = this.modalService.openModal<any, string[]>(BulkAsinModalComponent, {
      size: "md",
      height: "fill",
      modalTitle: this.bulkButtonLabel(),
      data: {
        catalog: this.catalog(),
        disabledAsins: this.disabledAsins(),
        selectedAsins: this.selectedAsins(),
        customList: this.customList(),
        allowOtherCatalog: this.allowOtherCatalog(),
      },
    });

    ref
      .afterClosed()
      .pipe(
        filter((asinList): asinList is string[] => !!asinList),
        untilDestroyed(this),
      )
      .subscribe((asinList) => this.addSelection(asinList));
  }

  private selectAll() {
    const asinToAdd = Array.from(this.filteredProductList().values())
      .flat()
      .filter((p) => !p.isDisabled && !p.isSelected)
      .map((p) => p.asin);
    this.addSelection(asinToAdd);
  }

  private addSelection(asins: string[]) {
    for (const asin of asins) {
      if (!this.catalog()?.contains(asin)) {
        this.otherCatalogAsins.update((s) => [asin, ...s]);
      }
    }
    let concatAsins = [...this.selectedAsins(), ...asins];
    if (this.asinsLimit() && concatAsins.length > this.asinsLimit()!) {
      concatAsins = concatAsins.slice(0, this.asinsLimit()!);
    }

    this.selectedAsins.update((s) => [...concatAsins]);
  }

  private unselectAll() {
    this.selectedAsins.set([]);
  }

  onFilterChange(filters: Filter<AsinFilter>[]) {
    this.asinFilterService.selectFilters(filters);
  }

  private getIneligibleReason(asin: string): string | undefined {
    if (this.ineligibilityTag() == undefined) {
      return undefined;
    }
    if (this.ineligibilityTag() === "SP" && !this.catalog()?.getSPEligibility().get(asin)?.status) {
      return this.catalog()?.getSPEligibility().get(asin)?.reason;
    }
    if (this.ineligibilityTag() === "SB" && !this.catalog()?.getSBEligibility().get(asin)?.status) {
      return this.catalog()?.getSBEligibility().get(asin)?.reason;
    }
    return undefined;
  }

  private getImageUrl(imageUrl: string | undefined): string | undefined {
    if (imageUrl) {
      if (imageUrl.includes(this.NO_IMAGE_URL_PART)) {
        return undefined;
      }
      return imageUrl;
    }
    return undefined;
  }

  private isPassingFilter(product: AsinSelectionProduct, text: string): boolean {
    const query = text.toLowerCase().trim();
    if (query.length === 0) {
      return true;
    }

    return !!(
      product.asin.toLowerCase().includes(query) ||
      product.title?.toLowerCase().includes(query) ||
      product.brand?.toLowerCase().includes(query)
    );
  }

  setOpenParentAsin(asin: string) {
    this.openedParentAsin.set(this.openedParentAsin() === asin ? undefined : asin);
  }

  private getProduct(
    asin: string,
    disabledAsins: Set<string> | undefined,
    selectedAsins: string[],
    catalog: Catalog,
  ): AsinSelectionProduct {
    let product = catalog.asinOffers.get(asin);
    if (product === undefined) {
      // If the product is not found in the account catalog, get the marketplace catalog
      product = this.notInCatalogProductDetails().get(asin);
    }
    const res: AsinSelectionProduct = {
      asin,
      title: product?.title,
      brand: product?.brand,
      image: this.getImageUrl(product?.imageUrl),
      isOtherCatalog:
        !catalog.isParentAsin(asin) && !this.managedOrUnmanagedAsins().has(asin) && !catalog.contains(asin),
      ineligibleReason: this.getIneligibleReason(asin),
      tag1: product?.productTag1,
      tag2: product?.productTag2,
      isParent: catalog.isParentAsin(asin),
      childAsins: catalog.getChildAsins(asin),
      isSelected: false,
      isDisabled: false,
      isUnmanaged: !catalog.contains(asin) && !catalog.isParentAsin(asin) && this.managedOrUnmanagedAsins().has(asin),
      isIndeterminate: false,
    };

    if (res.isParent) {
      res.isSelected = res.childAsins!.every((childAsin) => selectedAsins.includes(childAsin));
      res.isDisabled = res.childAsins!.some((childAsin) => disabledAsins?.has(childAsin));
      res.isIndeterminate = res.childAsins!.some((childAsin) => selectedAsins.includes(childAsin)) && !res.isSelected;
    } else {
      res.isSelected = selectedAsins.includes(asin);
      res.isDisabled = !!disabledAsins?.has(asin);
    }

    return res;
  }

  exportCsv() {
    const fields: CsvField<AsinSelectionProduct>[] = [
      simpleField("asin"),
      fieldExtractor("Title", (data) => data.title ?? ""),
      fieldExtractor("Brand", (data) => data.brand ?? ""),
      fieldExtractor("Image", (data) => data.image ?? ""),
      fieldExtractor("Tag1", (data) => data.tag1 ?? ""),
      fieldExtractor("Tag2", (data) => data.tag2 ?? ""),
      fieldExtractor("IneligibleReason", (data) => data.ineligibleReason ?? ""),
    ];

    this.csvExportService.exportCsv(
      {
        prefix:
          getCsvFileName("asin_list", this.accountMarketplace()?.accountId, this.accountMarketplace()?.marketplace) +
          "_asins",
      },
      [...this.filteredProductList().values()].flat(),
      fields,
    );
  }
}
